'use client';

import { useState } from 'react';

import { Medal } from '@/components/Medal';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ProfileTitleCard } from './ProfileTitleCard';
import type { ProfileTitlesData } from './ProfileTitles';

export function ProfileTitlesClient({
  titles,
}: {
  titles: ProfileTitlesData['titles'];
}) {
  const [positionFilter, setTitlePositionFilter] = useState('all');
  const [competitionFilter, setCompetitionFilter] = useState('all');

  const uniqueCompetitions = Array.from(
    new Set(
      titles.map((title) => {
        const competition = (title.matchday?.season!.competition ||
          title.season?.competition)!;
        return competition.parent?.name || competition.name;
      }),
    ),
  ).filter((name): name is string => Boolean(name));

  const filteredTitles = titles.filter((title) => {
    const positionMatch =
      positionFilter === 'all'
        ? true
        : title.position === Number(positionFilter);
    const competition = (title.matchday?.season!.competition ||
      title.season?.competition)!;
    const competitionName = competition.parent?.name || competition.name;
    const competitionMatch =
      competitionFilter === 'all'
        ? true
        : competitionName === competitionFilter;
    return positionMatch && competitionMatch;
  });

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 px-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="competition">Competición</Label>
          <Select
            value={competitionFilter}
            onValueChange={setCompetitionFilter}
          >
            <SelectTrigger id="competition" className="w-full sm:w-56">
              <SelectValue placeholder="Seleccionar competición" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {uniqueCompetitions.map((competitionName) => {
                const title = titles.find((title) => {
                  const competition = (title.matchday?.season!.competition ||
                    title.season?.competition)!;
                  const name = competition.parent?.name || competition.name;
                  return name === competitionName;
                })!;
                const competition = (title.matchday?.season!.competition ||
                  title.season?.competition)!;
                const displayName =
                  competition.parent?.name || competition.name;
                return (
                  <SelectItem key={competitionName} value={competitionName}>
                    {displayName}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="position-filter">Posición</Label>
          <ToggleGroup
            id="position-filter"
            type="single"
            value={positionFilter}
            onValueChange={(value) => value && setTitlePositionFilter(value)}
            className="flex justify-self-end border border-input shadow-xs w-full"
          >
            <ToggleGroupItem
              value="all"
              aria-label="Todas las medallas"
              className="px-4 h-[34px]"
            >
              Todas
            </ToggleGroupItem>
            <ToggleGroupItem
              value="1"
              aria-label="Medalla de oro"
              className="h-[34px]"
            >
              <Medal position={1} className="size-4" />
            </ToggleGroupItem>
            <ToggleGroupItem
              value="2"
              aria-label="Medalla de plata"
              className="h-[34px]"
            >
              <Medal position={2} className="size-4" />
            </ToggleGroupItem>
            <ToggleGroupItem
              value="3"
              aria-label="Medalla de bronce"
              className="h-[34px]"
            >
              <Medal position={3} className="size-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      <div className="px-4 pb-4 overflow-y-auto scroll-gradient">
        {filteredTitles.length > 0 ? (
          <ol className="flex flex-col gap-y-1">
            {filteredTitles.map((title) => (
              <li key={title.id}>
                <ProfileTitleCard data={title} />
              </li>
            ))}
          </ol>
        ) : (
          <div className="flex items-center justify-center text-center h-24">
            <p>No se encontraron medallas para el filtro seleccionado.</p>
          </div>
        )}
      </div>
    </>
  );
}
